import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FoundersPage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colors = {
    green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
    green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
    gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
    gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
    gray100: '#f3f4f6', gray50: '#f9fafb',
    amber600: '#d97706', amber500: '#f59e0b', amber400: '#fbbf24', amber300: '#fcd34d',
    amber100: '#fef3c7', amber50: '#fffbeb',
  };

  const perks = [
    { icon: '♾️', title: 'Unlimited Players', desc: 'No cap — ever. Grow your golf group as large as you want.' },
    { icon: '🔒', title: 'Rate Locked for Life', desc: '$49.99/year, forever. Even when prices increase for new subscribers.' },
    { icon: '🏅', title: 'Founders Badge', desc: 'Permanently recognized as a founding member of Tee Time Organizer.' },
    { icon: '⛳', title: 'All Features Included', desc: 'Scoring, live rounds, SMS reminders, GHIN integration, pairings — everything.' },
    { icon: '🆓', title: '30-Day Free Trial', desc: 'Try it completely free. No charge until your trial ends.' },
    { icon: '❌', title: 'Cancel Anytime', desc: 'No contracts, no hassle. Cancel with one click if it\'s not for you.' },
  ];

  const features = [
    'Tee time scheduling & publishing',
    'One-click player RSVP & waitlist',
    'Automated email & SMS reminders',
    'Weather forecasts on every tee time',
    'Pairings, flights & tee sheet generator',
    'Live scoring & post-round scorecards',
    'GHIN handicap index import',
    'Course handicap calculations (WHS)',
    'Player database & groups',
    'Skins, putts & games module',
    'Mobile PWA — no app download needed',
    'Message log & communication history',
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", background: 'white', minHeight: '100vh' }}>

      {/* Header */}
      <header style={{
        borderBottom: `1px solid ${colors.gray200}`,
        padding: isMobile ? '10px 16px' : '12px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'white', position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px', height: '36px', background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
            borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px'
          }}>⛳</div>
          <div>
            <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 800, color: colors.gray900, letterSpacing: '-0.5px' }}>Tee Time Organizer</div>
            {!isMobile && <div style={{ fontSize: '11px', color: colors.gray500, fontWeight: 500 }}>The golf group organizer that does everything</div>}
          </div>
        </div>
        <button
          onClick={() => navigate('/register?plan=founders')}
          style={{
            background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`,
            color: '#78350f', border: 'none', borderRadius: '8px',
            padding: isMobile ? '8px 14px' : '9px 20px',
            fontSize: isMobile ? '13px' : '14px', fontWeight: 700, cursor: 'pointer',
          }}
        >
          🏅 Claim Founders Spot
        </button>
      </header>

      {/* Hero */}
      <section style={{
        background: `linear-gradient(160deg, ${colors.amber50} 0%, white 50%, ${colors.green50} 100%)`,
        padding: isMobile ? '48px 20px 40px' : '72px 32px 60px',
        textAlign: 'center',
        borderBottom: `1px solid ${colors.gray200}`,
      }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: `linear-gradient(135deg, ${colors.amber400}, ${colors.amber500})`,
          color: '#78350f', borderRadius: '20px', padding: '6px 16px',
          fontSize: '13px', fontWeight: 700, marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
        }}>
          🏅 Exclusive Founders Offer — Limited Spots
        </div>

        <h1 style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: isMobile ? '34px' : '54px',
          fontWeight: 400, color: colors.gray900,
          lineHeight: 1.15, letterSpacing: '-1px',
          marginBottom: '20px', maxWidth: '700px', margin: '0 auto 20px',
        }}>
          Run Your Golf Group Like a <span style={{ color: colors.green700 }}>Pro</span> — For Life
        </h1>

        <p style={{
          fontSize: isMobile ? '16px' : '19px', color: colors.gray600,
          lineHeight: 1.65, maxWidth: '560px', margin: '0 auto 12px',
        }}>
          Tee Time Organizer handles scheduling, RSVPs, scoring, reminders, and more.
          Lock in our <strong style={{ color: colors.gray900 }}>Founders rate of $49.99/year</strong> before spots are gone.
        </p>

        <p style={{ fontSize: '14px', color: colors.gray400, marginBottom: '36px' }}>
          30-day free trial · No credit card required to start
        </p>

        {/* Pricing callout */}
        <div style={{
          display: 'inline-block',
          background: 'white',
          border: `2px solid ${colors.amber400}`,
          borderRadius: '20px',
          padding: isMobile ? '24px 28px' : '32px 48px',
          marginBottom: '36px',
          boxShadow: '0 8px 32px rgba(245,158,11,0.15)',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.amber600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            Founders Price
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '4px', marginBottom: '6px' }}>
            <span style={{ fontSize: isMobile ? '52px' : '64px', fontWeight: 800, color: colors.gray900, lineHeight: 1, letterSpacing: '-2px' }}>$49.99</span>
            <span style={{ fontSize: '18px', color: colors.gray500, fontWeight: 500, paddingBottom: '10px' }}>/year</span>
          </div>
          <div style={{ fontSize: '14px', color: colors.gray500, marginBottom: '4px' }}>
            Unlimited players · All features · Rate locked forever
          </div>
          <div style={{ fontSize: '13px', color: colors.green700, fontWeight: 600 }}>
            That's less than $4.17/month
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/register?plan=founders')}
            style={{
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
              color: 'white', border: 'none', borderRadius: '12px',
              padding: isMobile ? '16px 32px' : '18px 48px',
              fontSize: isMobile ? '16px' : '18px', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(21,128,61,0.35)',
              letterSpacing: '-0.3px',
            }}
          >
            🏅 Claim My Founders Spot — Start Free Trial
          </button>
          <p style={{ fontSize: '13px', color: colors.gray400, margin: 0 }}>
            30 days free, then $49.99/year · Cancel anytime
          </p>
        </div>
      </section>

      {/* Perks */}
      <section style={{ padding: isMobile ? '44px 20px' : '60px 32px', maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            borderRadius: '20px', padding: '5px 14px', fontSize: '13px', fontWeight: 600, marginBottom: '12px'
          }}>
            What You Get
          </div>
          <h2 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: isMobile ? '30px' : '38px', color: colors.gray900,
            letterSpacing: '-0.5px', marginBottom: '12px',
          }}>
            Everything in One Plan
          </h2>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray500, lineHeight: 1.6 }}>
            Founders get the full platform — no tiers, no upgrades, no surprises.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px',
        }}>
          {perks.map((perk, i) => (
            <div key={i} style={{
              background: 'white', border: `1px solid ${colors.gray200}`,
              borderRadius: '16px', padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <div style={{
                width: '48px', height: '48px', background: colors.amber50,
                borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px', marginBottom: '16px', border: `1px solid ${colors.amber100}`,
              }}>
                {perk.icon}
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: colors.gray900, marginBottom: '8px' }}>{perk.title}</h3>
              <p style={{ fontSize: '14px', color: colors.gray500, lineHeight: 1.6, margin: 0 }}>{perk.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature list */}
      <section style={{
        background: colors.gray50, borderTop: `1px solid ${colors.gray200}`, borderBottom: `1px solid ${colors.gray200}`,
        padding: isMobile ? '44px 20px' : '60px 32px',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            borderRadius: '20px', padding: '5px 14px', fontSize: '13px', fontWeight: 600, marginBottom: '12px'
          }}>
            Full Feature List
          </div>
          <h2 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: isMobile ? '30px' : '38px', color: colors.gray900,
            letterSpacing: '-0.5px', marginBottom: '36px',
          }}>
            The Complete Golf Group Toolkit
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '12px', textAlign: 'left',
          }}>
            {features.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'white', border: `1px solid ${colors.gray200}`,
                borderRadius: '10px', padding: '12px 16px',
              }}>
                <span style={{
                  width: '22px', height: '22px', background: colors.green100,
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', flexShrink: 0, color: colors.green700, fontWeight: 700,
                }}>✓</span>
                <span style={{ fontSize: '14px', color: colors.gray700, fontWeight: 500 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{
        padding: isMobile ? '48px 20px' : '72px 32px',
        textAlign: 'center',
        background: `linear-gradient(135deg, ${colors.green900} 0%, ${colors.green700} 100%)`,
      }}>
        <div style={{
          display: 'inline-block', background: 'rgba(255,255,255,0.15)', color: colors.amber300,
          borderRadius: '20px', padding: '5px 14px', fontSize: '13px', fontWeight: 700,
          marginBottom: '20px', border: '1px solid rgba(255,255,255,0.2)',
        }}>
          🏅 Limited Founders Spots Available
        </div>
        <h2 style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: isMobile ? '30px' : '44px', color: 'white',
          letterSpacing: '-0.5px', marginBottom: '16px', lineHeight: 1.2,
        }}>
          Lock In $49.99/Year, Forever
        </h2>
        <p style={{
          fontSize: isMobile ? '15px' : '18px', color: 'rgba(255,255,255,0.75)',
          lineHeight: 1.6, maxWidth: '480px', margin: '0 auto 36px',
        }}>
          Join as a Founder today and never pay more — even as we add features and raise prices for new subscribers.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/register?plan=founders')}
            style={{
              background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`,
              color: '#78350f', border: 'none', borderRadius: '12px',
              padding: isMobile ? '16px 32px' : '18px 48px',
              fontSize: isMobile ? '16px' : '18px', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            🏅 Claim My Founders Spot — Start Free Trial
          </button>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', margin: 0 }}>
            30 days free · $49.99/year after · Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? '20px 16px' : '24px 32px',
        borderTop: `1px solid ${colors.gray200}`,
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center', justifyContent: 'space-between', gap: '8px',
        background: 'white',
      }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: colors.gray900 }}>Tee Time Organizer</div>
        <div style={{ fontSize: '13px', color: colors.gray400, textAlign: 'center' }}>
          Questions? Email us at{' '}
          <a href="mailto:support@teetimeorganizer.net" style={{ color: colors.green700, textDecoration: 'none', fontWeight: 600 }}>
            support@teetimeorganizer.net
          </a>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <span onClick={() => navigate('/login')} style={{ fontSize: '13px', color: colors.gray500, cursor: 'pointer' }}>Sign In</span>
          <span onClick={() => navigate('/')} style={{ fontSize: '13px', color: colors.gray500, cursor: 'pointer' }}>Home</span>
        </div>
      </footer>

    </div>
  );
}
